import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react';
import { ToastQueue } from '@adobe/react-spectrum';
import styles from './Overview.module.scss';
import { RichTextEditorComponent } from 'app/components/RsdForms/CreateMoment/components/RichTextEditorComponent';
// Removed unused CheckmarkIcon import - now using Save button
import FigmaPixelPerfectOverviewField from '../components/FigmaPixelPerfectOverviewField';
import { AudienceStrategicAlignmentField } from '../components/AudienceStrategicAlignmentField';
import {
  ExistingOverviewStructure,
  mapExistingToNewOverview
} from '../../../../utils/overviewMapping';
import {
  EOL_EXCLUDED_OVERVIEW_FIELDS,
  overviewFieldRenderPolicies,
  getOverviewFieldLabel
} from '../configs/overviewFieldConfig';
import { isEolMoment } from 'utils/constants';
import { useRBAC } from '../context/RBACContext';
import { useMomentContext } from '../context/MomentContext';
import { updateMoment } from '../../RsdForms/api';
import { detectStrategicAlignmentError } from '../utils/strategicAlignmentErrorHandler';
import ToolTipWrapper from 'app/components/ToolTipWrapper/ToolTipWrapper';
import {
  SLIDEOUT_TOOLTIP_MAPPING,
  SLIDEOUT_PRODUCT_ZEN_TOOLTIP_MESSAGE
} from 'utils/constants';
import { isEmptyHtml, stripHtml } from '../CommentPanel/utils';
import type { AlertIssue } from '../../AlertsAndNotification/store/types';
import { useActiveAlertIssues } from '../../AlertsAndNotification/hooks/useActiveAlertIssues';
import { matchesOverviewAlertIssue } from '../../AlertsAndNotification/utils/declarativeHighlight';

const overviewKeySent = (
  values: ExistingOverviewStructure | undefined,
  key: keyof ExistingOverviewStructure
) => values != null && Object.prototype.hasOwnProperty.call(values, key);

const shouldRenderOverviewField = ({
  field,
  values,
  overviewConfig,
  momentType
}: {
  field: keyof ExistingOverviewStructure;
  values: ExistingOverviewStructure | undefined;
  overviewConfig: OverviewConfig;
  momentType?: string | null;
}) => {
  if (
    isEolMoment(momentType) &&
    (EOL_EXCLUDED_OVERVIEW_FIELDS as readonly string[]).includes(field)
  ) {
    return false;
  }

  const policy = overviewFieldRenderPolicies[field as string];

  if (policy?.requiresTemplateConfig) {
    return Boolean(overviewConfig?.[field]) && overviewKeySent(values, field);
  }

  return overviewKeySent(values, field) || Boolean(overviewConfig?.[field]);
};

interface MultiSelectRichTextValue {
  type: 'multi_select_richtext';
  options: string[];
  value: {
    selected: string[];
    content: string;
  };
}

interface OverviewFieldConfig {
  type: string;
  options?: string[];
}

interface OverviewConfig {
  [key: string]: OverviewFieldConfig | any; // Allow dynamic configs
}

interface OverviewProps {
  overviewConfig: OverviewConfig;
  values: ExistingOverviewStructure;
  issueId?: string; // Add issueId for API calls
  headlineUpdatedAt?: string; // Timestamp for headline updates
  updated?: string; // Timestamp for other overview fields
  onDataRefresh?: (patchResponse?: unknown) => void; // Add callback for seamless data refresh
  onNotificationRefresh?: (patchResponse?: unknown) => void; // Callback to refresh notifications after successful overview PATCH
  momentName?: string; // For audit logging
  momentType?: string; // For audit logging - RSD Type
  onUnsavedChanges?: (hasChanges: boolean) => void; // Callback to notify parent of unsaved changes
  onResetRequested?: () => void; // Callback to reset Overview state externally
  onStrategicAlignmentUpdated?: () => void; // Callback when strategic alignment is updated
  onStrategicAlignmentError?: (
    error: any,
    payload: Record<string, any>,
    intendedFieldValues?: Record<string, any>
  ) => void; // Callback when strategic alignment error is detected
}

// Ref interface for Overview component
export interface OverviewRef {
  highlightStrategicAlignment: () => void;
  clearStrategicAlignmentError: () => void;
  getCurrentFieldValues: () => ExistingOverviewStructure;
  /** Closes any field left open for editing (no-op if nothing is open). */
  closeOpenEditors: () => void;
}

const Overview = forwardRef<OverviewRef, OverviewProps>(
  (
    {
      overviewConfig,
      values,
      issueId,
      headlineUpdatedAt,
      updated,
      onDataRefresh,
      onNotificationRefresh,
      momentName,
      momentType,
      onUnsavedChanges,
      onStrategicAlignmentUpdated,
      onStrategicAlignmentError
    },
    ref
  ) => {
    // ✅ CENTRALIZED: Get moment metadata from context when available
    const { momentName: contextMomentName, momentType: contextMomentType } =
      useMomentContext();

    const overviewMomentType = contextMomentType;
    const { canEditOverview } = useRBAC();
    const [editingFields, setEditingFields] = useState<Set<string>>(new Set());
    // Bumped by closeOpenEditors() to force-remount FigmaPixelPerfectOverviewField /
    // AudienceStrategicAlignmentField instances closed, since their edit-open state is
    // local to those components (see their key props below).
    const [closeEditorsTrigger, setCloseEditorsTrigger] = useState(0);
    const [fieldValues, setFieldValues] =
      useState<ExistingOverviewStructure>(values);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [fieldTimestamps, setFieldTimestamps] = useState({
      headlineUpdatedAt: headlineUpdatedAt || '',
      updated: updated || ''
    });

    // Reset fieldValues when values prop changes (new RSD loaded)
    useEffect(() => {
      setFieldValues(values);
      setHasChanges(false); // Reset unsaved changes flag
    }, [values]);

    // Notify parent component when unsaved changes state changes
    useEffect(() => {
      onUnsavedChanges?.(hasChanges);
    }, [hasChanges, onUnsavedChanges]);
    const htmlContentRef = useRef<HTMLDivElement>(null);
    const [isMvpHighlighted, setIsMvpHighlighted] = useState(false);
    const activeAlertIssues = useActiveAlertIssues(issueId);

    // State for tracking expanded fields and content heights
    const [expandedFields, setExpandedFields] = useState<Set<string>>(
      new Set()
    );
    const [fieldHeights, setFieldHeights] = useState<Record<string, number>>(
      {}
    );
    const contentRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const fieldContainerRefs = useRef<Record<string, HTMLDivElement | null>>(
      {}
    );

    // Error state for strategic alignment field
    const [strategicAlignmentError, setStrategicAlignmentError] =
      useState(false);
    const strategicAlignmentFieldRef = useRef<HTMLDivElement | null>(null);

    const MAX_COLLAPSED_HEIGHT = 288;

    const getOverviewHighlightIssue = (field: string): AlertIssue | null => {
      if (field !== 'headline') return null;
      for (const issue of activeAlertIssues) {
        if (matchesOverviewAlertIssue(issue, 'headline')) {
          return issue;
        }
      }
      return null;
    };

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      highlightStrategicAlignment: () => {
        setStrategicAlignmentError(true);

        // Scroll to field
        setTimeout(() => {
          const fieldElement = strategicAlignmentFieldRef.current;
          if (fieldElement) {
            const scrollingContainer = document.querySelector(
              '[data-testid="slideout-content-container"]'
            ) as HTMLElement;

            if (scrollingContainer) {
              const fieldRect = fieldElement.getBoundingClientRect();
              const containerRect = scrollingContainer.getBoundingClientRect();
              const fieldRelativeTop = fieldRect.top - containerRect.top;

              if (
                fieldRelativeTop < 0 ||
                fieldRelativeTop > containerRect.height - 200
              ) {
                const targetScrollPosition =
                  scrollingContainer.scrollTop + fieldRelativeTop - 100;

                scrollingContainer.scrollTo({
                  top: Math.max(0, targetScrollPosition),
                  behavior: 'smooth'
                });
              }
            }

            // Focus the field container
            fieldElement.focus();
          }
        }, 300);
      },
      clearStrategicAlignmentError: () => {
        setStrategicAlignmentError(false);
      },
      getCurrentFieldValues: () => fieldValues,
      closeOpenEditors: () => {
        if (editingFields.size > 0) {
          Array.from(editingFields).forEach(field => handleCancel(field));
        }
        setCloseEditorsTrigger(prev => prev + 1);
      }
    }));

    // Listen for custom event from MVP milestone click to trigger highlight
    useEffect(() => {
      const handleMvpHighlight = () => {
        setIsMvpHighlighted(true);
        // Remove highlight after animation completes (match feature highlight duration)
        setTimeout(() => {
          setIsMvpHighlighted(false);
        }, 3000); // Match feature highlight duration (3 seconds)
      };

      window.addEventListener('highlightMvpSection', handleMvpHighlight);

      return () => {
        window.removeEventListener('highlightMvpSection', handleMvpHighlight);
      };
    }, []);

    // Update timestamps when props change
    useEffect(() => {
      setFieldTimestamps({
        headlineUpdatedAt: headlineUpdatedAt || '',
        updated: updated || ''
      });
    }, [headlineUpdatedAt, updated]);

    // Helper function to format timestamp for display
    const formatTimestamp = (timestamp: string | undefined) => {
      if (!timestamp) return '';
      try {
        const date = new Date(timestamp);
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch (error) {
        return '';
      }
    };

    const getFieldTimestamp = (field: string) =>
      field === 'headline'
        ? fieldTimestamps.headlineUpdatedAt
        : fieldTimestamps.updated;

    const markOverviewUpdated = () => {
      const currentTimestamp = new Date().toISOString();
      setFieldTimestamps(prev => ({
        ...prev,
        updated: currentTimestamp
      }));
    };

    const isDirectlyOpenableHref = (href: string): boolean =>
      /^(https?:|mailto:|tel:|blob:|data:|about:)/i.test(href);

    // Utility function to handle link clicks in rendered HTML content
    const handleLinkClick = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'A') {
        const anchor = target as HTMLAnchorElement;
        const href = anchor.getAttribute('href');

        if (href) {
          event.preventDefault();
          event.stopPropagation(); // Prevent edit handler from firing

          if (href.startsWith('mailto:') || href.startsWith('tel:')) {
            window.location.href = href;
          } else if (isDirectlyOpenableHref(href)) {
            window.open(href, '_blank', 'noopener,noreferrer');
          } else {
            // For all other links (including domain names without protocol), add https://
            const urlToOpen =
              href.startsWith('www.') || href.includes('.')
                ? `https://${href}`
                : href;
            window.open(urlToOpen, '_blank', 'noopener,noreferrer');
          }
        }
      }
    };

    // Enhanced click handler that handles both links and edit functionality
    const handleContentClick = (field: string) => (event: React.MouseEvent) => {
      const target = event.target as HTMLElement;

      // If user clicked on a link, don't trigger edit mode
      if (target.tagName === 'A' || target.closest('a')) {
        return; // Let the link click handler deal with it
      }

      // Otherwise, handle edit functionality
      if (canEditOverview) {
        handleEdit(field);
      }
    };

    // Add event listener for link clicks in rendered HTML content
    useEffect(() => {
      // Global click listener to handle link clicks
      const globalOverviewClickHandler = (event: Event) => {
        const target = event.target as HTMLElement;

        if (target.tagName === 'A') {
          const anchor = target as HTMLAnchorElement;
          const href = anchor.getAttribute('href');

          // Handle the link click directly
          if (href) {
            event.preventDefault();
            event.stopPropagation();

            if (href.startsWith('mailto:') || href.startsWith('tel:')) {
              window.location.href = href;
            } else if (isDirectlyOpenableHref(href)) {
              window.open(href, '_blank', 'noopener,noreferrer');
            } else {
              // For all other links (including domain names without protocol), add https://
              const urlToOpen =
                href.startsWith('www.') || href.includes('.')
                  ? `https://${href}`
                  : href;
              window.open(urlToOpen, '_blank', 'noopener,noreferrer');
            }
          }
        }
      };

      // Add global listener
      document.addEventListener('click', globalOverviewClickHandler, true);

      const currentRef = htmlContentRef.current;
      if (currentRef) {
        currentRef.addEventListener('click', handleLinkClick, true);

        return () => {
          document.removeEventListener(
            'click',
            globalOverviewClickHandler,
            true
          );
          currentRef.removeEventListener('click', handleLinkClick, true);
        };
      } else {
        return () => {
          document.removeEventListener(
            'click',
            globalOverviewClickHandler,
            true
          );
        };
      }
    }, []);

    // Update fieldValues when values prop changes (important for data consistency)
    useEffect(() => {
      setFieldValues(values);
    }, [values]);

    // Measure content heights to determine if "Read more" is needed
    useEffect(() => {
      const measureHeights = () => {
        const heights: Record<string, number> = {};
        Object.keys(contentRefs.current).forEach(field => {
          const ref = contentRefs.current[field];
          if (ref) {
            heights[field] = ref.scrollHeight;
          }
        });
        setFieldHeights(heights);
      };

      // Measure heights after content renders
      measureHeights();

      // Re-measure on window resize
      window.addEventListener('resize', measureHeights);
      return () => window.removeEventListener('resize', measureHeights);
    }, [fieldValues, editingFields]);

    // Toggle expand/collapse for a field
    const toggleExpanded = (field: string) => {
      const isExpanding = !expandedFields.has(field);

      if (isExpanding) {
        // Just expand
        setExpandedFields(prev => {
          const newSet = new Set(prev);
          newSet.add(field);
          return newSet;
        });
      } else {
        // When collapsing (Read Less clicked)
        setExpandedFields(prev => {
          const newSet = new Set(prev);
          newSet.delete(field);
          return newSet;
        });

        // After collapsing, scroll to show the field properly
        requestAnimationFrame(() => {
          const fieldContainer = fieldContainerRefs.current[field];

          if (fieldContainer) {
            // Find the scrolling container (the slideout content container)
            const scrollingContainer = document.querySelector(
              '[data-testid="slideout-content-container"]'
            ) as HTMLElement;

            if (scrollingContainer) {
              // Get the field's position relative to the scrolling container
              const fieldRect = fieldContainer.getBoundingClientRect();
              const containerRect = scrollingContainer.getBoundingClientRect();

              // Calculate the position of the field within the scrolling container
              const fieldRelativeTop = fieldRect.top - containerRect.top;

              // Check if the field is above the viewport or too far down
              if (
                fieldRelativeTop < 0 ||
                fieldRelativeTop > containerRect.height - 200
              ) {
                // Scroll the container to put the field at a comfortable reading position
                const targetScrollPosition =
                  scrollingContainer.scrollTop + fieldRelativeTop - 100;

                scrollingContainer.scrollTo({
                  top: Math.max(0, targetScrollPosition),
                  behavior: 'smooth'
                });
              }
            }
          }
        });
      }
    };

    // These are used by the click handlers in renderField
    const handleEdit = (field: string) => {
      setEditingFields(prev => new Set([...Array.from(prev), field]));
    };

    const handleCancel = (field: string) => {
      setEditingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(field);
        return newSet;
      });
    };

    // Helper function to revert a field to its saved value
    const revertFieldValue = (field: string, savedValue: any) => {
      setFieldValues(prev => ({
        ...prev,
        [field]: savedValue
      }));
      setHasChanges(false);
    };

    // Helper function to handle strategic alignment errors
    const handleStrategicAlignmentError = (
      error: any,
      payload: Record<string, any>,
      field: string,
      savedValue: any,
      intendedFieldValues?: Record<string, any>
    ) => {
      if (onStrategicAlignmentError) {
        onStrategicAlignmentError(error, payload, intendedFieldValues);
        revertFieldValue(field, savedValue);
      }
    };

    // Convert overview data to RSD form template format and submit to API
    const handleSubmit = async () => {
      if (!issueId) {
        return;
      }

      setIsSubmitting(true);

      const changedFields: Array<{
        field: string;
        oldValue: any;
        newValue: any;
      }> = [];

      try {
        // Check which fields changed
        Object.keys(fieldValues).forEach(field => {
          const oldVal = values[field];
          const newVal = fieldValues[field];
          if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            changedFields.push({
              field: field,
              oldValue: oldVal,
              newValue: newVal
            });
          }
        });

        // Convert current field values to new overview payload format
        const newOverviewPayload = mapExistingToNewOverview(fieldValues);
        // Validate that critical fields are properly structured
        const validateField = (field: any, fieldName: string) => {
          if (field && typeof field === 'object' && 'selected' in field) {
            return true;
          }
          return false;
        };

        // Log validation info for multi-select and dropdown richtext fields
        validateField(fieldValues.whyAreWeLaunching, 'whyAreWeLaunching');
        if (
          overviewConfig?.mvpStatus &&
          overviewKeySent(fieldValues as ExistingOverviewStructure, 'mvpStatus')
        ) {
          validateField(fieldValues.mvpStatus, 'mvpStatus');
        }

        // Add headlineUpdatedAt: new timestamp if headline changed, existing if not (prevents backend from overwriting on every PATCH)
        const currentTimestamp = new Date().toISOString();
        const headlineChanged = fieldValues.headline !== values.headline;
        const payloadWithTimestamps = {
          ...newOverviewPayload,
          ...(headlineChanged
            ? { headlineUpdatedAt: currentTimestamp }
            : fieldTimestamps.headlineUpdatedAt
            ? { headlineUpdatedAt: fieldTimestamps.headlineUpdatedAt }
            : {})
        };

        // 2. Call the moment patch API with overview data
        const response = await updateMoment(issueId, {
          overview: payloadWithTimestamps
        });
        // Update timestamps locally (no need to refetch - we already have the data)
        setFieldTimestamps(prev => ({
          ...prev,
          headlineUpdatedAt:
            fieldValues.headline !== values.headline
              ? currentTimestamp
              : prev.headlineUpdatedAt,
          updated: currentTimestamp
        }));

        // Trigger parent data refresh to keep values prop in sync
        if (onDataRefresh) {
          onDataRefresh(response);
        }
        if (onNotificationRefresh) {
          onNotificationRefresh(response);
        }

        // Reset changes flag after successful submission
        setHasChanges(false);
      } catch (error) {
        // Check if this is a strategic alignment error
        // Skip check if only updating the strategic alignment field itself
        const onlyUpdatingStrategicAlignment = Object.keys(fieldValues).every(
          key =>
            key === 'audienceStrategicAlignment' ||
            JSON.stringify(fieldValues[key]) === JSON.stringify(values[key])
        );

        const isStrategicAlignmentError =
          !onlyUpdatingStrategicAlignment &&
          detectStrategicAlignmentError(error);

        if (isStrategicAlignmentError) {
          // Store the payload for retry
          const payloadWithTimestamps = {
            ...mapExistingToNewOverview(fieldValues),
            ...(fieldValues.headline !== values.headline && {
              headlineUpdatedAt: new Date().toISOString()
            })
          };

          // Call the error handler (parent will handle highlighting and retry)
          if (onStrategicAlignmentError) {
            const intendedFieldValues: Record<string, any> = {};
            changedFields.forEach(change => {
              intendedFieldValues[change.field] = change.newValue;
            });
            onStrategicAlignmentError(
              error,
              {
                overview: payloadWithTimestamps
              },
              intendedFieldValues
            );
          }

          // Revert to last saved values on error
          setFieldValues(values);
          setHasChanges(false);
        } else {
          // Revert to last saved values on error
          setFieldValues(values);
          setHasChanges(false);

          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to update overview. Please try again.';
          ToastQueue.negative(errorMessage, { timeout: 5000 });
        }
      } finally {
        setIsSubmitting(false);
      }
    };

    const renderField = (label: string, field: keyof typeof fieldValues) => {
      const config = overviewConfig?.[field];
      const fieldValue = fieldValues?.[field];
      const fieldTimestamp = getFieldTimestamp(field);
      const showReadModeTimestamp =
        field === 'headline' && fieldTimestamp && !editingFields.has(field);
      const fieldPlaceholder: string =
        (SLIDEOUT_TOOLTIP_MAPPING as Record<string, string | undefined>)[
          label
        ] ??
        (field === 'productZen'
          ? SLIDEOUT_PRODUCT_ZEN_TOOLTIP_MESSAGE
          : 'Enter details here...');
      const isMultiSelectRichText = config?.type === 'multi_select_richtext';
      const isDropdownRichText = config?.type === 'dropdown_richtext';
      const isDropdown = config?.type === 'dropdown';
      const isAudienceStrategicAlignment =
        field === 'audienceStrategicAlignment';

      // Special handling for audienceStrategicAlignment field
      if (isAudienceStrategicAlignment) {
        // Helper function to check if at least one must-win option is selected
        const hasAtLeastOneMustWinOption = (
          mappings: Record<string, string[]>
        ) => {
          return Object.values(mappings || {}).some(
            options => Array.isArray(options) && options.length > 0
          );
        };

        return (
          <div
            data-testid={`overview-field-${field}`}
            className={`${styles.overviewField} ${
              strategicAlignmentError ? styles.fieldError : ''
            }`}
            ref={el => {
              fieldContainerRefs.current[field] = el;
              (
                strategicAlignmentFieldRef as React.MutableRefObject<HTMLDivElement | null>
              ).current = el;
            }}
            tabIndex={strategicAlignmentError ? 0 : -1}
          >
            <AudienceStrategicAlignmentField
              key={`audience-field-${closeEditorsTrigger}`}
              value={
                (fieldValue as any) || {
                  audienceStrategyMappings: {},
                  content: ''
                }
              }
              onChange={async newValue => {
                const oldValue = fieldValues[field] ||
                  values[field] || {
                    audienceStrategyMappings: {},
                    content: ''
                  };

                // Store the previous value for potential rollback
                const previousValue = { ...oldValue };

                // Update UI optimistically
                setFieldValues(prev => ({
                  ...prev,
                  [field]: newValue
                }));
                setHasChanges(true);

                // Check if at least one must-win option is selected
                const hasMustWinOptions = hasAtLeastOneMustWinOption(
                  newValue.audienceStrategyMappings || {}
                );

                // Clear error if user has selected at least one must-win option
                if (strategicAlignmentError && hasMustWinOptions) {
                  setStrategicAlignmentError(false);
                }

                // Make instant API call for dropdown changes (not rich text content changes)
                if (
                  issueId &&
                  JSON.stringify(newValue.audienceStrategyMappings) !==
                    JSON.stringify(
                      (oldValue as any)?.audienceStrategyMappings
                    ) &&
                  newValue.content === (oldValue as any)?.content
                ) {
                  // Define payload outside try block for error handling
                  const updatedFieldValues = {
                    ...fieldValues,
                    [field]: newValue
                  };
                  const completeOverviewPayload = {
                    ...mapExistingToNewOverview(updatedFieldValues),
                    ...(fieldTimestamps.headlineUpdatedAt
                      ? { headlineUpdatedAt: fieldTimestamps.headlineUpdatedAt }
                      : {})
                  };

                  try {
                    const response = await updateMoment(issueId, {
                      overview: completeOverviewPayload
                    });
                    markOverviewUpdated();

                    // ✅ REMOVED: onDataRefresh() call to prevent jittery behavior
                    // The local state is already updated optimistically, so no need to refetch
                    if (onNotificationRefresh) {
                      onNotificationRefresh(response);
                    }

                    setHasChanges(false);

                    // Check if we should retry tier update
                    if (hasMustWinOptions && onStrategicAlignmentUpdated) {
                      // Small delay to ensure API has processed the update
                      setTimeout(() => {
                        onStrategicAlignmentUpdated();
                      }, 500);
                    }
                  } catch (error) {
                    // Skip strategic alignment error check for ALL audienceStrategicAlignment field changes
                    // (dropdowns and rich text content - no error check for this field)
                    // Revert UI state on API error
                    revertFieldValue(field, previousValue);

                    // Show error message to user
                    const errorMessage =
                      error instanceof Error
                        ? error.message
                        : 'Failed to update strategic alignment. Please try again.';
                    ToastQueue.negative(errorMessage, { timeout: 5000 });
                  }
                }
              }}
              label={label}
              placeholder={fieldPlaceholder}
              disabled={!canEditOverview}
              lastUpdated={fieldTimestamp}
              onSubmit={async () => {
                if (issueId) {
                  const oldValue = values[field] || {
                    audienceStrategyMappings: {},
                    content: ''
                  };
                  const newValue = fieldValues[field] || {
                    audienceStrategyMappings: {},
                    content: ''
                  };
                  // Check if at least one must-win option is selected
                  const hasMustWinOptions = hasAtLeastOneMustWinOption(
                    (newValue as any)?.audienceStrategyMappings || {}
                  );

                  // Clear error if user has selected at least one must-win option
                  if (strategicAlignmentError && hasMustWinOptions) {
                    setStrategicAlignmentError(false);
                  }

                  // Define payload outside try block for error handling
                  const completeOverviewPayload = {
                    ...mapExistingToNewOverview(fieldValues),
                    ...(fieldTimestamps.headlineUpdatedAt
                      ? { headlineUpdatedAt: fieldTimestamps.headlineUpdatedAt }
                      : {})
                  };

                  try {
                    const response = await updateMoment(issueId, {
                      overview: completeOverviewPayload
                    });
                    markOverviewUpdated();

                    if (onDataRefresh) {
                      onDataRefresh(response);
                    }
                    if (onNotificationRefresh) {
                      onNotificationRefresh(response);
                    }

                    setHasChanges(false);

                    // Check if we should retry tier update
                    if (hasMustWinOptions && onStrategicAlignmentUpdated) {
                      setTimeout(() => {
                        onStrategicAlignmentUpdated();
                      }, 500);
                    }
                  } catch (error) {
                    // Skip strategic alignment error check for ALL audienceStrategicAlignment field changes
                    // (dropdowns and rich text content - no error check for this field)
                    // Revert to last saved values on error
                    revertFieldValue(field, values[field]);

                    const errorMessage =
                      error instanceof Error
                        ? error.message
                        : 'Failed to update strategic alignment. Please try again.';
                    ToastQueue.negative(errorMessage, { timeout: 5000 });
                  }
                }
              }}
              onCancel={() => {
                // Only revert rich text content; preserve audience/must-win selections
                const savedContent =
                  ((values as any)[field] as any)?.content || '';
                setFieldValues(prev => {
                  const current = (prev[field] as any) || {};
                  return {
                    ...prev,
                    [field]: {
                      audienceStrategyMappings:
                        current.audienceStrategyMappings || {},
                      content: savedContent
                    }
                  };
                });
                setHasChanges(false);
              }}
              savedContent={((values as any)[field] as any)?.content || ''}
            />
          </div>
        );
      }

      // If no config found, don't render as dropdown (fallback to text)
      if (!config) {
      }

      // For dropdown fields (including simple dropdown), use the new pixel-perfect component
      if (isMultiSelectRichText || isDropdownRichText || isDropdown) {
        return (
          <FigmaPixelPerfectOverviewField
            key={`figma-field-${field}-${closeEditorsTrigger}`}
            field={{
              id: field,
              name: field,
              label,
              type: isMultiSelectRichText
                ? 'multi_select_richtext'
                : isDropdownRichText
                ? 'dropdown_richtext'
                : 'dropdown',
              options: config.options || [],
              api: config.api,
              required: false
            }}
            value={{
              options: isMultiSelectRichText
                ? (fieldValue as any)?.selected || []
                : undefined,
              option:
                isDropdownRichText || isDropdown
                  ? (fieldValue as any)?.selected?.[0] || ''
                  : undefined,
              content: (fieldValue as any)?.content || ''
            }}
            disabled={field === 'mvpStatus' || !canEditOverview} // MVP Status read-only OR no edit permission
            onChange={async (fieldName, value) => {
              // ✅ 1. Capture OLD value BEFORE any state updates
              const oldValue = fieldValues[field] || values[field] || '';

              // Update local state immediately for instant UI feedback
              // All dropdown fields should store in the same format with selected array
              const newFieldValue = {
                selected: isMultiSelectRichText
                  ? value.options || []
                  : value.option
                  ? [value.option]
                  : [],
                content: value.content || ''
              };

              setFieldValues(prev => ({
                ...prev,
                [field]: newFieldValue
              }));
              setHasChanges(true);

              // Make instant API call ONLY for dropdown changes (not rich text content changes)
              if (
                issueId &&
                (value.options !== undefined || value.option !== undefined) &&
                value.content === (fieldValue as any)?.content
              ) {
                // Create updated fieldValues with the new field value for API call
                const updatedFieldValues = {
                  ...fieldValues,
                  [field]: newFieldValue
                };

                // Convert updated fieldValues to the complete overview API format
                // Define payload outside try block for error handling
                const completeOverviewPayload = {
                  ...mapExistingToNewOverview(updatedFieldValues),
                  ...(fieldTimestamps.headlineUpdatedAt
                    ? { headlineUpdatedAt: fieldTimestamps.headlineUpdatedAt }
                    : {})
                };

                try {
                  // Send complete overview payload
                  const response = await updateMoment(issueId, {
                    overview: completeOverviewPayload
                  });
                  markOverviewUpdated();

                  // Trigger seamless data refresh
                  if (onDataRefresh) {
                    onDataRefresh(response);
                  }
                  if (onNotificationRefresh) {
                    onNotificationRefresh(response);
                  }

                  // Reset hasChanges since we've saved to API
                  setHasChanges(false);
                } catch (error) {
                  // Check if this is a strategic alignment error
                  // (Note: this handler doesn't process strategic alignment field, so check is always valid)
                  const isStrategicAlignmentError =
                    detectStrategicAlignmentError(error);

                  if (isStrategicAlignmentError) {
                    handleStrategicAlignmentError(
                      error,
                      { overview: completeOverviewPayload },
                      field,
                      oldValue,
                      { [field]: newFieldValue }
                    );
                  } else {
                    // Revert to previous value on error
                    revertFieldValue(field, oldValue);

                    const errorMessage =
                      error instanceof Error
                        ? error.message
                        : 'Failed to update overview. Please try again.';
                    ToastQueue.negative(errorMessage, { timeout: 5000 });
                  }
                }
              }
            }}
            label={label}
            placeholder={fieldPlaceholder}
            lastUpdated={fieldTimestamp}
            onSubmit={async () => {
              // Handle rich text content submission with full overview payload
              if (issueId) {
                const newValue = fieldValues[field] || '';
                // Convert current fieldValues to the complete overview API format
                // Define payload outside try block for error handling
                const completeOverviewPayload = {
                  ...mapExistingToNewOverview(fieldValues),
                  ...(fieldTimestamps.headlineUpdatedAt
                    ? { headlineUpdatedAt: fieldTimestamps.headlineUpdatedAt }
                    : {})
                };

                try {
                  // Send complete overview payload
                  const response = await updateMoment(issueId, {
                    overview: completeOverviewPayload
                  });
                  markOverviewUpdated();

                  // Trigger parent data refresh to keep values prop in sync
                  if (onDataRefresh) {
                    onDataRefresh(response);
                  }
                  if (onNotificationRefresh) {
                    onNotificationRefresh(response);
                  }

                  // Reset hasChanges since we've saved to API
                  setHasChanges(false);
                } catch (error) {
                  // Check if this is a strategic alignment error
                  // (Note: this handler doesn't process strategic alignment field, so check is always valid)
                  const isStrategicAlignmentError =
                    detectStrategicAlignmentError(error);

                  if (isStrategicAlignmentError) {
                    handleStrategicAlignmentError(
                      error,
                      { overview: completeOverviewPayload },
                      field,
                      values[field],
                      { [field]: newValue }
                    );
                  } else {
                    // Revert to last saved values on error
                    revertFieldValue(field, values[field]);

                    const errorMessage =
                      error instanceof Error
                        ? error.message
                        : 'Failed to update overview. Please try again.';
                    ToastQueue.negative(errorMessage, { timeout: 5000 });
                  }
                }
              }
            }}
            onCancel={() => {
              // Revert parent local state to last SAVED value
              setFieldValues(prev => ({
                ...prev,
                [field]: values[field]
              }));
              setHasChanges(false);
            }}
            savedContent={
              typeof (values as any)[field] === 'string'
                ? ((values as any)[field] as string)
                : ((values as any)[field] as any)?.content || ''
            }
          />
        );
      }

      const overviewHighlightIssue = getOverviewHighlightIssue(field);
      return (
        <div
          data-testid={`overview-field-${field}`}
          className={`${styles.overviewField} ${
            field === 'headline' ? styles.headlineField : ''
          } ${showReadModeTimestamp ? styles.hasTimestamp : ''}${
            overviewHighlightIssue ? ' alert-field-highlight' : ''
          }`}
          {...(overviewHighlightIssue && issueId
            ? {
                'data-alert-issue-id': issueId,
                'data-alert-field-ref':
                  overviewHighlightIssue.fieldRef ?? 'null',
                'data-alert-rule-id': overviewHighlightIssue.ruleId
              }
            : {})}
          ref={el => {
            fieldContainerRefs.current[field] = el;
          }}
          // style={{
          //   background: field === 'headline' ? '#f8f8f8' : '',
          //   color: field === 'headline' ? '#ffffff' : ''
          // }}
        >
          <div
            data-testid={`overview-field-label-${field}`}
            className={styles.fieldLabel}
            // style={{ color: field === 'headline' ? '#ffffff' : '' }}
          >
            <span>{label}</span>
            {/* <ToolTipWrapper message={SLIDEOUT_TOOLTIP_MAPPING[label] ?? ''}>
          </ToolTipWrapper> */}
          </div>
          <div
            data-testid={`overview-field-value-${field}`}
            className={styles.fieldValue}
            style={{
              cursor: 'pointer'
            }}
          >
            {editingFields.has(field) ? (
              <div
                style={{
                  width: '100%'
                }}
              >
                <RichTextEditorComponent
                  field={{
                    id: field,
                    name: field,
                    label,
                    required: false,
                    type: 'richtext',
                    charLimit: config?.charLimit
                  }}
                  value={
                    typeof fieldValue === 'string'
                      ? fieldValue
                      : fieldValue?.content || ''
                  }
                  onChange={(_, value) => {
                    setFieldValues(prev => ({ ...prev, [field]: value }));
                    setHasChanges(true);
                  }}
                  externalTimestamp={
                    field === 'headline'
                      ? fieldTimestamps.headlineUpdatedAt
                      : fieldTimestamps.updated
                  }
                  minHeight="300px"
                  hideLabel
                  footer={
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className={styles.cancelButton}
                        style={{
                          background: 'var(--Palette-gray-200, #E6E6E6)',
                          border: 'none',
                          borderRadius: '16px',
                          width: '57px',
                          height: '24px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          padding: '0 12px',
                          fontFamily: 'Adobe Clean',
                          fontSize: '13px',
                          fontWeight: 700,
                          color: 'var(--Alias-content-neutral-default, #222)',
                          minWidth: 'auto'
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          // Revert changes for this field
                          setFieldValues(prev => ({
                            ...prev,
                            [field]: values[field]
                          }));
                          setHasChanges(false);
                          // Close the editor
                          handleCancel(field);
                        }}
                        aria-label="Cancel"
                      >
                        Cancel
                      </button>
                      <button
                        className={styles.saveButton}
                        style={{
                          background: '#1473E6',
                          border: 'none',
                          borderRadius: '16px',
                          width: '57px',
                          height: '24px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          padding: '0 12px',
                          fontFamily: 'Adobe Clean',
                          fontSize: '13px',
                          fontWeight: 700,
                          color: '#FFFFFF',
                          minWidth: 'auto'
                        }}
                        onClick={async e => {
                          e.stopPropagation();
                          handleCancel(field);

                          // If this is the headline field, immediately update the timestamp
                          if (
                            field === 'headline' &&
                            fieldValues.headline !== values.headline
                          ) {
                            const currentTimestamp = new Date().toISOString();
                            setFieldTimestamps(prev => ({
                              ...prev,
                              headlineUpdatedAt: currentTimestamp
                            }));
                          }

                          // Trigger submit when done editing
                          await handleSubmit();
                        }}
                        aria-label="Save"
                      >
                        Save
                      </button>
                    </div>
                  }
                />
              </div>
            ) : (
              <>
                <div
                  ref={htmlContentRef}
                  onClick={handleContentClick(field)}
                  style={{
                    cursor: canEditOverview ? 'pointer' : 'default',
                    width: '100%',
                    minHeight: '24px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexDirection: 'column'
                  }}
                >
                  <div
                    ref={el => {
                      contentRefs.current[field] = el;
                    }}
                    className={`${styles.fieldContent} ${
                      !expandedFields.has(field) &&
                      fieldHeights[field] > MAX_COLLAPSED_HEIGHT
                        ? styles.collapsed
                        : ''
                    }`}
                    style={{
                      maxHeight:
                        !expandedFields.has(field) &&
                        fieldHeights[field] > MAX_COLLAPSED_HEIGHT
                          ? `${MAX_COLLAPSED_HEIGHT}px`
                          : 'none',
                      overflow: 'hidden',
                      width: '100%'
                    }}
                  >
                    <span
                      className={
                        !fieldValue ||
                        (typeof fieldValue === 'string' &&
                          isEmptyHtml(fieldValue)) ||
                        (typeof fieldValue === 'object' &&
                          isEmptyHtml(fieldValue?.content || ''))
                          ? 'overview-placeholder-text'
                          : ''
                      }
                      dangerouslySetInnerHTML={{
                        __html:
                          typeof fieldValue === 'string'
                            ? isEmptyHtml(fieldValue)
                              ? fieldPlaceholder
                              : fieldValue
                            : isEmptyHtml(fieldValue?.content || '')
                            ? fieldPlaceholder
                            : fieldValue?.content ?? ''
                      }}
                    />
                  </div>
                  {fieldHeights[field] > MAX_COLLAPSED_HEIGHT && (
                    <button
                      className={styles.readMoreButton}
                      onClick={e => {
                        e.stopPropagation();
                        toggleExpanded(field);
                      }}
                      aria-label={
                        expandedFields.has(field) ? 'Read Less' : 'Read More'
                      }
                    >
                      {expandedFields.has(field) ? 'Read Less' : 'Read More'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          {showReadModeTimestamp && (
            <span className={styles.timestamp}>
              {formatTimestamp(fieldTimestamp)}
            </span>
          )}
        </div>
      );
    };

    return (
      <div data-testid="overview-section" className={styles.overviewSection}>
        {overviewKeySent(values, 'headline') &&
          renderField('HEADLINE / CRITICAL UPDATE', 'headline')}
        <div
          data-testid="overview-fields-except-headline"
          className={styles.overviewFieldsExceptHeadline}
        >
          {shouldRenderOverviewField({
            field: 'productZen',
            values,
            overviewConfig,
            momentType: overviewMomentType
          }) &&
            renderField(
              (overviewConfig?.productZen as { label?: string } | undefined)
                ?.label || 'PRODUCT ZEN',
              'productZen'
            )}
          {overviewKeySent(values, 'whatAreWeLaunching') &&
            renderField(
              getOverviewFieldLabel(
                'WHAT ARE WE LAUNCHING?',
                'whatAreWeLaunching',
                overviewMomentType
              ),
              'whatAreWeLaunching'
            )}
          {shouldRenderOverviewField({
            field: 'whyAreWeLaunching',
            values,
            overviewConfig,
            momentType: overviewMomentType
          }) && renderField('WHY ARE WE LAUNCHING?', 'whyAreWeLaunching')}
          {overviewKeySent(values, 'howIsItDifferent') &&
            renderField(
              getOverviewFieldLabel(
                'HOW IS IT DIFFERENT?',
                'howIsItDifferent',
                overviewMomentType
              ),
              'howIsItDifferent'
            )}
          {overviewKeySent(values, 'whereWillItLaunch') &&
            renderField(
              getOverviewFieldLabel(
                'Where will it launch?',
                'whereWillItLaunch',
                overviewMomentType
              ),
              'whereWillItLaunch'
            )}
          {overviewKeySent(values, 'audienceStrategicAlignment') &&
            renderField(
              getOverviewFieldLabel(
                'WHO IS THIS FOR / STRATEGIC ALIGNMENT',
                'audienceStrategicAlignment',
                overviewMomentType
              ),
              'audienceStrategicAlignment'
            )}
          {shouldRenderOverviewField({
            field: 'mvpStatus',
            values,
            overviewConfig,
            momentType: overviewMomentType
          }) ? (
            <div
              id="mvp-section"
              data-testid="mvp-status-section"
              className={`${styles.mvpSectionWrapper} ${
                isMvpHighlighted ? styles.mvpSectionHighlight : ''
              }`}
            >
              {/* MVP Status - Status text (read-only) + Rich text editor (editable) */}
              <div
                data-testid="overview-field-mvpStatus"
                className={styles.overviewField}
                ref={el => {
                  fieldContainerRefs.current['mvpStatus'] = el;
                }}
              >
                <div
                  data-testid="overview-field-label-mvpStatus"
                  className={styles.fieldLabel}
                >
                  <span>MVP STATUS</span>
                </div>
                <div
                  data-testid="overview-field-value-mvpStatus"
                  className={styles.fieldValue}
                  style={{ display: 'block' }}
                >
                  {/* Part 1: Status dropdown value (read-only, always shown) */}
                  <div
                    style={{
                      fontFamily: 'Adobe Clean',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: '150%',
                      color: '#222',
                      marginBottom: '12px'
                    }}
                  >
                    {(() => {
                      const mvpStatusValue =
                        fieldValues?.mvpStatus || values?.mvpStatus;
                      const selectedOption = (mvpStatusValue as any)
                        ?.selected?.[0];
                      return selectedOption || 'N/A';
                    })()}
                  </div>

                  {/* Part 2: Rich text content (read mode) or editor (edit mode) */}
                  <div
                    style={{
                      cursor: editingFields.has('mvpStatus')
                        ? 'default'
                        : 'pointer'
                    }}
                    onClick={() => {
                      if (canEditOverview && !editingFields.has('mvpStatus')) {
                        handleEdit('mvpStatus');
                      }
                    }}
                  >
                    {editingFields.has('mvpStatus') ? (
                      <div
                        style={{
                          width: '100%'
                        }}
                        onClick={e => e.stopPropagation()}
                      >
                        <RichTextEditorComponent
                          field={{
                            id: 'mvpStatus',
                            name: 'mvpStatus',
                            label: 'MVP STATUS',
                            required: false,
                            type: 'richtext'
                          }}
                          value={
                            typeof (
                              fieldValues?.mvpStatus || values?.mvpStatus
                            ) === 'string'
                              ? fieldValues?.mvpStatus || values?.mvpStatus
                              : (
                                  (fieldValues?.mvpStatus ||
                                    values?.mvpStatus) as any
                                )?.content || ''
                          }
                          onChange={(_, value) => {
                            const mvpStatusValue =
                              fieldValues?.mvpStatus || values?.mvpStatus;
                            setFieldValues(prev => ({
                              ...prev,
                              mvpStatus: {
                                selected:
                                  (mvpStatusValue as any)?.selected || [],
                                content: value
                              }
                            }));
                            setHasChanges(true);
                          }}
                          externalTimestamp={fieldTimestamps.updated}
                          minHeight="300px"
                          hideLabel
                          footer={
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                className={styles.cancelButton}
                                style={{
                                  background:
                                    'var(--Palette-gray-200, #E6E6E6)',
                                  border: 'none',
                                  borderRadius: '16px',
                                  width: '57px',
                                  height: '24px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  padding: '0 12px',
                                  fontFamily: 'Adobe Clean',
                                  fontSize: '13px',
                                  fontWeight: 700,
                                  color:
                                    'var(--Alias-content-neutral-default, #222)',
                                  minWidth: 'auto'
                                }}
                                onClick={e => {
                                  e.stopPropagation();
                                  setFieldValues(prev => ({
                                    ...prev,
                                    mvpStatus: values.mvpStatus
                                  }));
                                  setHasChanges(false);
                                  handleCancel('mvpStatus');
                                }}
                                aria-label="Cancel"
                              >
                                Cancel
                              </button>
                              <button
                                className={styles.saveButton}
                                style={{
                                  background: '#1473E6',
                                  border: 'none',
                                  borderRadius: '16px',
                                  width: '57px',
                                  height: '24px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  padding: '0 12px',
                                  fontFamily: 'Adobe Clean',
                                  fontSize: '13px',
                                  fontWeight: 700,
                                  color: '#FFFFFF',
                                  minWidth: 'auto'
                                }}
                                onClick={async e => {
                                  e.stopPropagation();
                                  handleCancel('mvpStatus');
                                  await handleSubmit();
                                }}
                                aria-label="Save"
                              >
                                Save
                              </button>
                            </div>
                          }
                        />
                      </div>
                    ) : (
                      <div
                        ref={el => {
                          contentRefs.current['mvpStatus'] = el;
                        }}
                        className={`${styles.fieldContent} ${
                          !expandedFields.has('mvpStatus') &&
                          fieldHeights['mvpStatus'] > MAX_COLLAPSED_HEIGHT
                            ? styles.collapsed
                            : ''
                        }`}
                        style={{
                          maxHeight:
                            !expandedFields.has('mvpStatus') &&
                            fieldHeights['mvpStatus'] > MAX_COLLAPSED_HEIGHT
                              ? `${MAX_COLLAPSED_HEIGHT}px`
                              : 'none',
                          overflow: 'hidden',
                          width: '100%'
                        }}
                      >
                        {(() => {
                          const mvpStatusValue =
                            fieldValues?.mvpStatus || values?.mvpStatus;
                          const content = (mvpStatusValue as any)?.content;
                          const isEmpty =
                            !content || !content.trim() || isEmptyHtml(content);

                          if (isEmpty) {
                            // Show placeholder text when content is empty
                            return (
                              <span className="overview-placeholder-text">
                                {SLIDEOUT_TOOLTIP_MAPPING['MVP STATUS'] ||
                                  'Enter MVP status details here...'}
                              </span>
                            );
                          }

                          // Show actual content with word wrapping
                          return (
                            <div
                              ref={htmlContentRef}
                              className={styles.htmlContent}
                              style={{
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'normal'
                              }}
                              dangerouslySetInnerHTML={{ __html: content }}
                            />
                          );
                        })()}
                      </div>
                    )}

                    {/* Read More / Read Less button */}
                    {!editingFields.has('mvpStatus') &&
                      fieldHeights['mvpStatus'] > MAX_COLLAPSED_HEIGHT && (
                        <button
                          className={styles.readMoreButton}
                          onClick={e => {
                            e.stopPropagation();
                            toggleExpanded('mvpStatus');
                          }}
                        >
                          {expandedFields.has('mvpStatus')
                            ? 'Read Less'
                            : 'Read More'}
                        </button>
                      )}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
);

Overview.displayName = 'Overview';

export default Overview;

export type { MultiSelectRichTextValue };
